import type { InjectionKey, MaybeRefOrGetter } from 'vue';
import type { BlockEditorDrag } from '#layers/block-engine/composables/block-editor/drag';
import { computed, inject, onMounted, onScopeDispose, provide, ref, shallowReactive, toValue, useId } from 'vue';

/** Canvas-local Teleport target; optional for standalone editor renderers and detached fallback previews. */
const dragPreviewTeleportTargetKey: InjectionKey<ReturnType<typeof provideBlockDragPreviewTeleportTarget>> = Symbol('block-drag-preview-teleport-target');

/** One canvas owns its preview target and mounted sources; registrations never outlive their Vue scope. */
export function provideBlockDragPreviewTeleportTarget(drag: Pick<BlockEditorDrag, 'draggedBlock'>) {
	const targetMounted = ref(false);
	const sources = shallowReactive(new Map<symbol, MaybeRefOrGetter<string | null>>());
	const activeSource = computed(() => {
		const dragged = drag.draggedBlock.value;
		if (dragged?.source !== 'canvas') return null;

		for (const [instance, childId] of sources) {
			if (toValue(childId) === dragged.childId) return instance;
		}

		return null;
	});

	const teleportTarget = {
		/** Canvas-scoped DOM id used by Teleport's selector. */
		targetId: `block-drag-preview-${useId()}`,
		/** Mounted source supplying this canvas drag, or null for fallback previews. */
		activeSource,
		/** Call during target component setup; availability follows its mounted lifetime. */
		registerTarget() {
			onMounted(() => {
				targetMounted.value = true;
			});

			onScopeDispose(() => {
				targetMounted.value = false;
			});
		},
		/** Register one mounted instance and release its claim with its component scope. */
		registerSource(childId: MaybeRefOrGetter<string | null>) {
			const instance = Symbol('drag-preview-source');
			sources.set(instance, childId);
			onScopeDispose(() => sources.delete(instance));
			return computed(() => targetMounted.value && activeSource.value === instance);
		},
	} as const;

	provide(dragPreviewTeleportTargetKey, teleportTarget);
	return teleportTarget;
}

/** Access the nearest canvas Teleport target without sharing DOM ownership across editors. */
export function useBlockDragPreviewTeleportTarget() {
	return inject(dragPreviewTeleportTargetKey, null);
}
